import { generatePath } from '@/services/router/generate-path';
import { lzQueryEncode, queryEncode } from '@/utils/condition-query-encoding';
import type { CampaignTriggerUpdate, ObjectTriggerLeaf } from '@/services/db/campaign/mutations';
import type { NewsletterRecipientsUpdate, NewsletterSendingOptions } from '@/services/db/newsletter/mutations';
import type { DataIndexItemRef } from './item';

/**
 * Seeding a new record from a data-index item.
 *
 * A segment is not created here at all: the segment builder takes its
 * starting conditions from the URL, so "use this attribute in a segment"
 * is a link carrying an encoded condition tree. The campaign and one-time
 * send are real writes and live in `services/db`.
 */

/** Seconds in a day, as the condition serializer counts a relative timestamp. */
const SECONDS_IN_DAY = 86_400;

/**
 * The one attribute the segment builder will not accept an `exists` on.
 * It is restricted to timestamp operators there, so a seeded condition has
 * to arrive as one or the segment is invalid on first render.
 */
const CREATED_IN_CIO_AT = '_created_in_customerio_at';

type SerializedFilter = {
  field: string;
  operator: string;
  value: string | null;
  inverse: boolean;
};

type SerializedConditions = {
  and: unknown[];
};

/**
 * The conditions a new segment starts with, in the shape the segment
 * builder decodes.
 *
 * An attribute serializes as an `attribute_change` event with a `to` and a
 * `from` leaf, which is how that builder encodes "this attribute" — not as
 * a bare attribute leaf. An event serializes as itself with an empty
 * filter. Both carry the default refinement the builder writes for a new
 * condition (`times: 1`, `within: 0`).
 */
export function segmentConditionsForItem(item: DataIndexItemRef): SerializedConditions {
  switch (item.kind) {
    case 'object-attribute':
    case 'object-relationship-attribute':
      // Taken from the item rather than an optional argument: the leaf
      // needs a real object type, and `type_id: 0` is how the backend
      // spells a person attribute — a seeded segment carrying it opens
      // and then refuses to save.
      return objectSegmentConditions(item.id, item.object_type_id);
    case 'event':
      return { and: [refinedEvent('event', item.id, {})] };
    case 'attribute':
      return { and: [refinedEvent('attribute_change', item.id, { and: attributeLeaves(item.id) })] };
    default: {
      const _exhaustive: never = item;
      throw new Error(`unhandled data-index kind: ${String(_exhaustive)}`);
    }
  }
}

/** The filters an event condition carries, empty for a bare event. */
type SerializedEventFilters = { and: SerializedFilter[] } | Record<string, never>;

/** An event condition, with the refinement a freshly added one carries. */
function refinedEvent(type: string, name: string, filters: SerializedEventFilters) {
  return { event: { type, name, filters }, times: 1, within: 0, inverse: false };
}

/**
 * The `to`/`from` pair an attribute condition serializes to. `from` is the
 * inverse of `to`: "it is this now, and was not before".
 */
function attributeLeaves(name: string): SerializedFilter[] {
  if (name === CREATED_IN_CIO_AT) {
    // "in the last 1 day", the only shape this attribute accepts.
    const value = String(-1 * SECONDS_IN_DAY);
    return [
      { field: 'to', operator: 'timestamp_gt', value, inverse: false },
      { field: 'from', operator: 'timestamp_gt', value, inverse: true },
    ];
  }
  return [
    { field: 'to', operator: 'exists', value: null, inverse: false },
    { field: 'from', operator: 'exists', value: null, inverse: true },
  ];
}

/**
 * An object or relationship attribute seeds a relationship condition
 * refined by the attribute itself.
 *
 * The source is `relationship_attribute` for both kinds. The page this
 * replaces builds the filter from the condition type it passed in, and it
 * passes `relationship` for an object attribute too
 * (`utils/data-index/shared.ts:641-648` against `addFilter`), so the
 * object branch of that helper is unreachable from here.
 */
function objectSegmentConditions(name: string, objectTypeId: number): SerializedConditions {
  return {
    and: [
      {
        and: [
          { relationship: { type_id: objectTypeId, inverse: false } },
          {
            and: [
              {
                relationship_attribute: {
                  field: name,
                  operator: 'exists',
                  value: null,
                  inverse: false,
                  // The filter names its object type as well as the
                  // relationship above it does
                  // (`serialize/attribute.ts:94`, for an
                  // `ObjectAttributeCondition`).
                  type_id: objectTypeId,
                },
              },
            ],
          },
        ],
      },
    ],
  };
}

/** Where the segment builder opens, seeded with the item's condition. */
export function newSegmentHref(workspaceId: number, item: DataIndexItemRef): string {
  const conditions = lzQueryEncode(segmentConditionsForItem(item));
  return generatePath(
    '/workspaces/:workspaceId/journeys/segments/explore',
    { workspaceId: String(workspaceId) },
    { conditions },
  );
}

/**
 * What a new automation starts as, before it is told what triggers it.
 *
 * An event carries its own trigger on the create — the `event` field is
 * the trigger — so it needs nothing further. Every attribute kind is
 * created untyped or as an attribute campaign and then told, because a
 * `seg_attr` campaign's trigger has to be set against a saved record
 * (`~/code/ui/app/utils/data-index/shared.ts:806-822`).
 */
export function campaignCreateFields(item: DataIndexItemRef): CampaignCreateFields {
  const description = 'Created using the Data Index';

  if (item.kind === 'event') {
    return {
      name: `${item.id} event triggered automation`,
      type: 'transactional',
      description,
      event: item.id,
      event_type: 'event',
    };
  }

  return {
    name: `${item.id} attribute triggered automation`,
    // An object or relationship attribute cannot be a `seg_attr` trigger,
    // so it is created with no type and given an object one by the update.
    type: item.kind === 'attribute' ? 'seg_attr' : 'none',
    description,
  };
}

/** What a new automation is created with, before it is told what triggers it. */
export type CampaignCreateFields = {
  name: string;
  type: string;
  description: string;
  event?: string;
  event_type?: string;
};

/** The object-trigger and audience values the page this replaces writes. */
const OBJECT_TRIGGER_UPDATED = 2;
const AUDIENCE_RELATED = 1;

/**
 * The second write: what makes a new automation about the item.
 *
 * Returns nothing for an event, which was already triggered by its
 * create. The shapes are the ones the page this replaces writes, pinned
 * by its own producer suite — note that the object and relationship
 * trigger leaves carry no `value` key, unlike the rows the trigger editor
 * writes (`~/code/ui/app/utils/dryad/ledger/data-index-producers.md`).
 */
export function campaignTriggerUpdate(item: DataIndexItemRef): CampaignTriggerUpdate | null {
  // Narrowed on the way in rather than by elimination: the two object
  // kinds share one union member, and only a positive check reaches the
  // `object_type_id` on it.
  if (item.kind === 'object-attribute' || item.kind === 'object-relationship-attribute') {
    const campaign = { object_type_id: item.object_type_id, type: OBJECT_TRIGGER_UPDATED };
    // `person_filters` and `relationship_filters` are base64 condition
    // strings on the wire, not arrays, and both are omitempty — an audience
    // with no filters leaves them out rather than sending an empty one.
    const audience = { type: AUDIENCE_RELATED };

    return item.kind === 'object-relationship-attribute'
      ? {
          update_type: 'recipients',
          type: 'relationship',
          relationship_campaign: campaign,
          relationship_attribute_triggers: queryEncode([
            objectTriggerLeaf(item.id, 'relationship_attribute', item.object_type_id),
          ]),
          audience,
        }
      : {
          update_type: 'recipients',
          type: 'object',
          object_campaign: campaign,
          object_attribute_triggers: queryEncode([objectTriggerLeaf(item.id, 'object_attribute', item.object_type_id)]),
          audience,
        };
  }

  // An event is already triggered by its own create.
  if (item.kind === 'event') {
    return null;
  }

  return {
    update_type: 'recipients',
    // The backend picks which recipients fields to read off `type`, so
    // an update that omits it never binds the trigger below.
    type: 'seg_attr',
    trigger: { and: [{ attribute: { field: item.id, operator: 'exists', value: null, inverse: false } }] },
  };
}

/** One trigger leaf. No `value` key, as the producer writes none. */
function objectTriggerLeaf(field: string, type: string, typeId: number): ObjectTriggerLeaf {
  return { field, operator: 'exists', type, type_id: typeId };
}

/**
 * The second write for a one-time send: the recipients it goes to.
 *
 * As with the campaign triggers, the leaf carries no `value`
 * (`~/code/ui/app/utils/data-index/shared.ts:870-877`).
 *
 * **It does not branch on kind, and that is deliberate parity, not an
 * oversight.** The page this replaces writes this same attribute leaf for
 * an event too — `createNewsletterForItem` takes an attribute or an event
 * and never looks at which (`shared.ts:852-878`) — so a send made from an
 * event targets people carrying an *attribute* of that name, not people
 * who performed it. Its siblings here do branch, because Ember's do. This
 * is FPS-4154, recorded for the owner rather than fixed, because fixing it
 * means inventing the event-filter shape with no Ember producer to check
 * it against.
 */
export function newsletterRecipientsUpdate(
  item: DataIndexItemRef,
  options: NewsletterSendingOptions,
): NewsletterRecipientsUpdate {
  return {
    update_type: 'recipients',
    filters: queryEncode([{ type: 'attribute', field: item.id, operator: 'exists' }]),
    // Restated rather than chosen: the handler requires all four on a
    // recipients update, and these are the values the send was created with.
    send_percentage: options.send_percentage,
    send_to_unsubscribed: options.send_to_unsubscribed,
    deduped: options.deduped,
    use_message_limits: options.use_message_limits,
  };
}
